import * as React from "react"
import {Check, ChevronsUpDown, CircleAlert, CircleCheck, CircleX, LoaderCircle, RefreshCw} from "lucide-react"

import {cn} from "@/lib/utils"
import {Button} from "@/components/ui/button"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import axios, {AxiosResponse} from "axios";
import {useCallback, useEffect} from "react";
import {Company, CompanyAutocomplete, CompanyDto} from "@/types/Company";
import {toast} from "sonner";
import {debounce} from "lodash";
import {useParams} from "react-router";
import {Label} from "@/components/ui/label.tsx";

export default function CompanyComboBox({
                                            registrationNumber, onCompanyChange
                                        }: {
    registrationNumber: string,
    onCompanyChange: (registrationNumber: string, company: Company) => void
}) {
    const [open, setOpen] = React.useState(false)
    const [inputValue, setInputValue] = React.useState("")
    const [autoCompleteValues, setAutoCompleteValues] = React.useState<CompanyAutocomplete[]>([])
    const [value, setValue] = React.useState("")
    const [isLoading, setIsLoading] = React.useState(false)
    const params = useParams()
    const documentId = params.id

    useEffect(() => {
        if (registrationNumber !== value && registrationNumber) {
            setValue(registrationNumber)
            selectCompany({address: "", name: {url: "", value: ""}, taxNumber: "", registrationNumber}).then(
                (company) => {
                    setAutoCompleteValues([{
                        address: company.summary.details.headquarter,
                        name: {url: "", value: company.summary.details.name},
                        taxNumber: company.summary.details.taxNumber.value,
                        registrationNumber
                    }])
                }
            )
        }
        if (registrationNumber === undefined) {
            setValue("")
            setInputValue("")
        }
    }, [registrationNumber, documentId]);

    function search(input: string) {
        if(input && input.length > 2) {
            axios.get("/api/company/autocomplete", {params: {query: input}}).then((res) => {
                setAutoCompleteValues(res.data)
            }).catch(() => {
                toast.error("Frissítse az oldalt és próbálja meg újra!")
            })
        }
    }

    const debounceFn = useCallback(debounce(search, 450), []);

    function onInputchange(s: string) {
        setInputValue(s)
        debounceFn(s)
    }

    async function selectCompany(company: CompanyAutocomplete) {
        setIsLoading(true)
        const res = await axios.get<any, AxiosResponse<CompanyDto, any>, any>("/api/company/" + company.registrationNumber)
        onCompanyChange(company.registrationNumber, res.data.data)
        setIsLoading(false)
        return res.data.data
    }

    function reloadCompany(registrationNumber: string) {
        setIsLoading(true)
        axios.put("/api/company/" + registrationNumber).then((res) => {
            if (res.status === 200) {
                toast.success("Cégadatok sikeresen frissítve")
                onCompanyChange(registrationNumber, res.data.data)
            }
        }).catch(() => {
            toast.error("Frissítse az oldalt és próbálja meg újra!")
        }).finally(() => setIsLoading(false))
    }

    return (
        <div className={"w-full flex mb-4"}>
            <Label className={"w-full"}>
                Cégnév
                <div className={"w-full flex"}>
                    <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={open}
                                className="w-full justify-between"
                            >
                                {value
                                    ? autoCompleteValues.find((val) => val.registrationNumber === value)?.name.value
                                    : "Cég betöltése"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50"/>
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-96 p-0">
                            <Command filter={() => 1}>
                                <CommandInput value={inputValue} onValueChange={onInputchange}
                                              placeholder="Cég keresése"/>
                                <CommandList key={JSON.stringify(autoCompleteValues)}>
                                    <CommandEmpty>Nem található cég.</CommandEmpty>
                                    <CommandGroup>
                                        {autoCompleteValues.map((val, index) => {
                                            const statusIcons = {
                                                "checked-circle-fill": CircleCheck,
                                                "delete-circle-fill": CircleX,
                                                "alert-circle-fill": CircleAlert,
                                            } as const
                                            const StatusIcon = val.status
                                                ? statusIcons[val.status.icon.key as keyof typeof statusIcons] ?? CircleAlert
                                                : null
                                            return (
                                            <CommandItem
                                                key={val.registrationNumber + index}
                                                value={val.registrationNumber}
                                                onSelect={(currentValue) => {
                                                    setValue(currentValue === value ? "" : currentValue)
                                                    setOpen(false)
                                                    selectCompany(autoCompleteValues[index])
                                                }}
                                                className={"flex items-start gap-2"}
                                            >
                                                {StatusIcon && (
                                                    <StatusIcon
                                                        className={"mt-0.5 h-4 w-4 shrink-0"}
                                                        style={{color: val.status!.icon.color}}
                                                    />
                                                )}
                                                <div className={"flex min-w-0 flex-1 flex-col"}>
                                                    <div className={"flex items-center gap-2"}>
                                                        <span className={"truncate font-medium"}>{val.name.value}</span>
                                                        {val.status && (
                                                            <span className={"shrink-0 text-xs text-muted-foreground"}>
                                                                {val.status.value}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {(val.taxNumber || val.registrationNumber) && (
                                                        <span className={"text-xs text-muted-foreground"}>
                                                            {[val.taxNumber, val.registrationNumber].filter(Boolean).join(" · ")}
                                                        </span>
                                                    )}
                                                </div>
                                                <Check
                                                    className={cn(
                                                        "mt-0.5 ml-auto h-4 w-4 shrink-0",
                                                        value === val.registrationNumber ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                            </CommandItem>
                                            )
                                        })}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                    <Button variant={"outline"} className={"ml-2"} onClick={() => {
                        if (registrationNumber)
                            reloadCompany(registrationNumber)
                    }}>
                        {isLoading ? <LoaderCircle className={"animate-spin"}/> : <RefreshCw/>}
                    </Button>
                </div>
            </Label>
        </div>
    )
}
